import { CheckOutlined, LeftOutlined, StarFilled } from '@ant-design/icons';
import { Col, Dropdown, Image, Input, Modal, Row, Space, Switch } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { thunks } from '../../thunks/auth';
import { toTitleCase } from '../../utils/helper';
import { ContactModal, CustomModal } from '../Modals/CustomModal';
import NavbarMobile from '../Navbar/NavbarMobile';
import './_header-strip.scss';
import { ChatLines, HeadsetHelp, NavArrowDown, UserCircle, Download } from 'iconoir-react';
import { SecondaryButton } from '../UI/Button/Button';
import { authActions } from '../../store/auth';
import { trackData } from '../../utils/trackers';
import { getClaimEscalationLevels } from '../../utils/escalationMatrix';
import AppDownloadModal from '../Modals/AppDownloadModal';
import { SupportDrawer } from '../UI/Drawer/SupportDrawer';
import { multiEntityActions, Organisation } from '../../store/multiEntity';
import searchIcon from '../../assets/icons/search.svg';
import PencilIcon from '../../assets/icons/profile/pencil.svg?react';
import InterrogationIcon from '../../assets/icons/profile/interrogation.svg?react';
import ExitIcon from '../../assets/icons/profile/exit.svg?react';
import { useScrollState } from '../../hooks/useScrollState';
import { getEvenFeature } from '../../hooks/useEvenRedirect';
import { COLORS } from '../../constants/designTokens';

interface Props {
  canSwitchToEmployee?: boolean;
}

const HeaderStrip: React.FC<Props> = ({ canSwitchToEmployee }) => {
  const isScrolled = useScrollState();
  const dispatch = useDispatch(),
    [openContact, setOpenContact] = useState<boolean>(false),
    [supportDrawerVisible, setSupportDrawerVisible] = useState<boolean>(false),
    logoutHandler = () => {
      dispatch(thunks.logout());
    },
    { entities, chosenEntity, canChooseEntity } = useSelector((state: any) => state.multiEntity),
    [filteredEntities, setFilteredEntities] = useState<Organisation[]>(entities),
    themeDetails = useSelector((state: any) => state.theme),
    [showEntities, setShowEntities] = useState<boolean>(false),
    navigate = useNavigate(),
    [modalVisible, setModalVisible] = useState(false),
    name = useSelector((state: any) => state.auth.name),
    [visible, setVisible] = useState(false),
    { isCommercialOnly } = useSelector((state: any) => state.data),
    relationship_managers = useSelector((state: any) => state.auth.relationship_managers),
    activeMode = useSelector((state: any) => state.auth.activeMode),
    isEmployer = useSelector((state: any) => state.auth.roles)?.includes('employer'),
    mobileAppLinks = useSelector((state: any) => state.theme?.mobileAppLinks),
    hasAppLinks = mobileAppLinks?.is_enabled && (mobileAppLinks?.ios || mobileAppLinks?.android),
    [isAppModalVisible, setIsAppModalVisible] = useState(false),
    onChangeHandler = (val: 'employer' | 'employee') => {
      if (val === 'employee') {
        dispatch(authActions.changeActiveMode('employer'));
        trackData('Toggle Role', { 'Toggled To': 'employer' });
      } else {
        handleChooseEntity();
        dispatch(authActions.changeActiveMode('employee'));
        trackData('Toggle Role', { 'Toggled To': 'employee' });
      }
    },
    navigateToKnowledgeCenter = () => {
      let url = 'https://learn.zohopublic.in/external/manual/member-knowledge-base/article/manual-client-dashboard-employee?p=ceeef6965bc937132fb23f08ade6a696c22c654f33a88f1463f4ff6ae520cfd3';
      if (activeMode === 'employer') {
        url = 'https://learn.zohopublic.in/external/manual/gbp-partnerclient-knowledge-base/article/employer-handguide-getting-started?p=ceeef6965bc937132fb23f08ade6a696161bc54aabfd18e35f246f1ef7130518';
      }
      window.open(url, '_blank');
    },
    entityMenu = <div className="entity-menu">
      <div className="title">Enterprises</div>
      {entities?.slice(0, 3)?.map((entity: Organisation, index: number) => (
        <div key={index} className={`item item-text ${chosenEntity?.organisation_uuid === entity.organisation_uuid ? 'active' : ''}`} onClick={() => handleChooseEntity(entity.organisation_uuid)}>
          <Row style={{ width: '100%' }} justify="space-between" align="middle">
            <Col>
              {entity.organisation_name}
            </Col>
            <Col>
              {entity.is_default && <StarFilled style={{ color: themeDetails?.primaryColor }} />}
            </Col>
          </Row>
        </div>
      ))}
      {entities?.length > 3 && <div className="cta" onClick={() => { setShowEntities(true) }}>
        View All
      </div>}
      {showEntities && <Modal
        className="entity-list-modal"
        width="640px"
        visible={showEntities}
        closable={true}
        footer={false}
        destroyOnClose
        title="Enterprises"
        centered
        onCancel={() => { setFilteredEntities(entities); setShowEntities(false) }}>
        <div className="entity-search">
          <Input
            className="search-input"
            allowClear
            placeholder="Search by name"
            prefix={<img src={searchIcon} alt="search" />}
            width={'100%'}
            onChange={(e) => {
              if (!e.target.value || e.target.value === '') {
                setFilteredEntities(entities);
              } else {
                setFilteredEntities(entities?.filter((entity: Organisation) => entity.organisation_name.toLowerCase().includes(e.target.value.toLowerCase())));
              }
            }}
          />
        </div>
        <div className="entities">
          {filteredEntities?.map((entity: Organisation, index: number) => (
            <div key={index} className={`entity-item ${chosenEntity?.organisation_uuid === entity.organisation_uuid ? 'active' : ''}`} onClick={() => handleChooseEntity(entity.organisation_uuid)}>
              <Row style={{ width: '100%' }} justify="space-between" align="middle">
                <Col>
                  {entity.organisation_name}
                </Col>
                <Col>
                  {entity.is_default && <StarFilled style={{ color: themeDetails?.primaryColor }} />}
                  {entity?.organisation_uuid === chosenEntity?.organisation_uuid && <CheckOutlined style={{ color: themeDetails?.primaryColor }} />}
                </Col>
              </Row>
            </div>
          ))}
        </div>
      </Modal>}
    </div>,
    handleChooseEntity = (organisation_uuid?: string) => {
      if (organisation_uuid) {
        dispatch(multiEntityActions.setChosenEntity(entities.find((entity: Organisation) => entity.organisation_uuid === organisation_uuid)));
      } else {
        dispatch(multiEntityActions.setChosenEntity(entities.find((entity: Organisation) => entity.is_default)));
      }
    },
    menu = () => {
      return (
        <div className="header-menu">
          {(isEmployer && !isCommercialOnly && !canSwitchToEmployee) && <div className='item'>
            <SecondaryButton block onClick={() => onChangeHandler(activeMode)}>
              SWITCH TO {activeMode === 'employee' ? 'ADMIN' : 'MEMBER'}
            </SecondaryButton>
          </div>}
          {canSwitchToEmployee && <div className='item'>
            <SecondaryButton block onClick={() => onChangeHandler('employer')}>
              SWITCH TO MEMBER
            </SecondaryButton>
          </div>}
          {canChooseEntity && entityMenu}
          <div className="item item-text" onClick={() => navigate('profile')}>
            <PencilIcon />
            Profile
          </div>
          <div className="item item-text" onClick={navigateToKnowledgeCenter}>
            <InterrogationIcon />
            Help Center
          </div>
          <div className="item item-text" onClick={() => setModalVisible(true)}>
            <ExitIcon />
            Logout
          </div>
        </div>
      );
    },
    closeNavbar = () => {
      setVisible(false);
    },
    location = useLocation(),
    pathSegments = useMemo(() => {
      const segments = location.pathname.split('/').filter((segment) => segment);
      if (segments[0] === 'members' && (segments[1] === 'invite-member' || segments[1] === 'add-member')) {
        return ['members'];
      }
      return segments;
    }, [location.pathname]),
    isUUID = (str: string) => {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      return uuidRegex.test(str);
    },
    // Embedded partner apps (Gift Cards, Even) have their own in-app navigation, so the header is just the title.
    embeddedAppTitle = location.pathname.startsWith('/gift-cards')
      ? 'Gift Cards'
      : location.pathname.startsWith('/even/')
        ? getEvenFeature(location.pathname)?.title
        : undefined,
    title = ((): { isCustom?: boolean; value: string } => {
      if (embeddedAppTitle) return { value: embeddedAppTitle, isCustom: true };
      if (pathSegments.length === 0) return { value: 'Home', isCustom: true };
      const lastSegment = pathSegments[pathSegments.length - 1];

      if (pathSegments.includes('claims') && pathSegments.length > 1) {
        if (isUUID(lastSegment) || lastSegment === 'undefined') {
          const claimId = (location?.state as { claim: { claim_id: string } })?.claim?.claim_id;
          return { value: `Claim Details${claimId ? `: ${claimId}` : ''}`, isCustom: true };
        }
      }

      if (!isNaN(Number(lastSegment))) {
        if (pathSegments.length > 1) return { value: pathSegments[pathSegments.length - 2], isCustom: false };
        return { value: 'Home', isCustom: true };
      }

      return { value: lastSegment, isCustom: false };
    })(),
    isHomeInURL = location.pathname.includes('home') || location.pathname.includes('dashboard'),
    brandName = useSelector((state: any) => state.theme.brandName);

  // Screens that can be opened from several places (Home quick links / "View All", the Claims tab):
  // when the opener passed `state.from` (the Home quick links do), Back returns to that screen instead of the
  // parent URL. Without it (direct visit, Claims tab) Back keeps the parent URL, or Home for /explore.
  const returnsToPreviousScreen = ['/explore', '/claims/add-claim'].includes(location.pathname);

  const goBack = () => {
    if (returnsToPreviousScreen) {
      if (typeof (location.state as { from?: string } | null)?.from === 'string') {
        navigate(-1);
        return;
      }
      if (location.pathname === '/explore') {
        navigate('/home', { replace: true });
        return;
      }
    }
    if (pathSegments.length > 1) {
      // Remove the last segment and join the segments to form the new URL
      pathSegments.pop();
      const newPath = '/' + pathSegments.join('/');
      if (newPath?.includes('how-to-claim')) {
        navigate('claims');
      } else {
        navigate(newPath);
      }
    }
  };

  useEffect(() => {
    setFilteredEntities(entities);
  }, [entities]);

  return (
    <>
      {visible && <NavbarMobile close={closeNavbar} visible={visible} />}

      <CustomModal
        isModalVisible={modalVisible}
        confirmHandler={logoutHandler}
        cancelHandler={() => {
          setModalVisible(false);
        }}
        title={`Are you sure you want to logout?`}
        cancelButtonText="NO"
        confirmButtonText="YES"
      />
      <ContactModal isModalVisible={openContact} cancelHandler={() => setOpenContact(false)} data={
        {
          ...(activeMode === 'employer' && {
            relationship_manager: {
              full_name: relationship_managers?.policy_manager?.full_name,
              phone: relationship_managers?.policy_manager?.phone,
              email: relationship_managers?.policy_manager?.email,
            }
          }),
          claim_managers: getClaimEscalationLevels(relationship_managers),
          claim_manager: relationship_managers?.claim_manager,
        }
      } />
      <AppDownloadModal visible={isAppModalVisible} onClose={() => setIsAppModalVisible(false)} />
      <SupportDrawer
        visible={supportDrawerVisible}
        onClose={() => setSupportDrawerVisible(false)}
        title="We've got you covered!"
      />
      <div className="header-strip-parent">
        <Row justify="space-between" align="middle" className="header-strip header-web">
          <Col>
            <Image
              className="pointer header-logo"
              src={themeDetails?.logoUrl}
              preview={false}
              onClick={() => navigate('/')}
            />
          </Col>
          <Col className="header-web">
            <Row align="middle" gutter={24}>
              {hasAppLinks && activeMode === 'employee' && (
                <Col>
                  <div className='download-app-button pointer' onClick={() => setIsAppModalVisible(true)}>
                    <Download />
                    <span>DOWNLOAD APP</span>
                  </div>
                </Col>
              )}
              <Col>
                <Row className='icon-button-with-text button-shadow pointer' align='middle' gutter={8} onClick={() => setOpenContact(true)}>
                  <Col>
                    <HeadsetHelp />
                  </Col>
                  <Col>
                    SUPPORT
                  </Col>
                </Row>
              </Col>
              <Col>
                <div className="vertical-pipe"></div>
              </Col>
              <Col className='pointer'>
                <Dropdown trigger={['click']} overlay={menu}>
                  <Row className='icon-button-with-text pointer' align='middle' gutter={8}>
                    <Col style={{ display: 'flex', alignItems: 'center' }}>
                      <UserCircle />
                    </Col>
                    <Col>
                      <div className="header-user-name">{name}</div>
                      {(chosenEntity?.organisation_name || brandName) && (
                        <div className="header-user-org">{chosenEntity?.organisation_name || brandName}</div>
                      )}
                    </Col>
                    <Col style={{ display: 'flex', alignItems: 'center' }}>
                      <NavArrowDown /></Col>
                  </Row>
                </Dropdown>
              </Col>
            </Row>
          </Col>
        </Row>

        {isHomeInURL && (
          <Row
            justify="space-between"
            align="middle"
            className={`header-strip show-on-mobile home-header ${isScrolled ? 'is-scrolled' : ''}`}
          >
            <Col>
              <Image
                className="pointer header-logo"
                src={themeDetails?.logoUrl}
                preview={false}
                onClick={() => navigate('/')}
              />
            </Col>
            <Col>
              <button
                type="button"
                className="header-need-help"
                onClick={() => {
                  trackData('Need Help Clicked', { Location: 'Mobile header', Mode: activeMode });
                  // Employers get the support drawer (account + claims managers); members get the helpline.
                  if (activeMode === 'employer') setSupportDrawerVisible(true);
                  else setOpenContact(true);
                }}
              >
                <ChatLines width={22} height={22} />
                <span>Need Help?</span>
              </button>
            </Col>
          </Row>
        )}

        {!isHomeInURL && (
          <Row justify="start" align="middle" className={`header-strip show-on-mobile major-screen-header ${isScrolled ? 'is-scrolled' : ''}`}>
            {!embeddedAppTitle && (pathSegments.length > 1 || returnsToPreviousScreen) && (
              <Col style={{ display: 'flex', alignItems: 'center' }}>
                <span className="back-button" onClick={goBack}>
                  <LeftOutlined style={{ color: COLORS.primaryText }} />
                </span>
              </Col>
            )}
            <Col>
              <span className="title">
                {title?.isCustom ? title?.value : toTitleCase(title?.value?.replaceAll('-', ' '))}
              </span>
            </Col>
          </Row>
        )}
      </div>
    </>
  );
};

export default HeaderStrip;
